// ENT-6257: the one home of the supplier-profile provenance copy (approved copy
// matrix, D3) and the public-sources disclaimer text. Pure: no Supabase import.
// `ProfileProvenance` is read from the server (vendor_profiles.profile_provenance
// or the marketplace read model's profileProvenance); never derive it here.

export type ProfileProvenance = 'supplier_attested' | 'supply_guard_managed' | 'public_sources';

const PROFILE_PROVENANCES: readonly ProfileProvenance[] = [
  'supplier_attested',
  'supply_guard_managed',
  'public_sources',
];

/**
 * ENT-6257 AC5: a Supply Guard managed profile has no claimant organisation, so no workspace's
 * supplier context ever resolves to it. Its operators reach it through their own accepted
 * vendor-team membership (the only team rows a managed profile keeps; the migration's guards
 * refuse any other). The vendor workspace guards admit exactly that case; reads stay enforced by
 * the backend's vendor-team policies.
 */
export function isManagedProfileOperator(
  profile: { stewardship?: string | null } | null | undefined,
  userRole: string | null | undefined,
): boolean {
  return profile?.stewardship === 'supply_guard' && Boolean(userRole);
}

/** Owner-scoped vendor readers answer this for a managed profile's operator (ENT-6257 AC5). */
export const MANAGED_PROFILE_READER_UNAVAILABLE = 'Not available while Supply Guard maintains this profile';

/** Display only: these surfaces need an owning organisation, and the backend refuses them anyway. */
export function isSupplyGuardManagedProfile(profile: { stewardship?: string | null } | null | undefined): boolean {
  return profile?.stewardship === 'supply_guard';
}

export function isSupplyGuardManagedReaderResponse(data: unknown): boolean {
  return typeof data === 'object'
    && data !== null
    && (data as { availability?: unknown }).availability === 'supply_guard_managed';
}

export function isProfileProvenance(value: unknown): value is ProfileProvenance {
  return typeof value === 'string' && (PROFILE_PROVENANCES as readonly string[]).includes(value);
}

export const SUPPLIER_PROFILE_DISCLAIMER_TITLE = 'Disclaimer – Publicly Available Supplier Information';

export const SUPPLIER_PROFILE_DISCLAIMER_PARAGRAPHS: readonly string[] = [
  'This supplier profile has been compiled and is maintained by Supply Guard using information obtained from publicly available sources and third-party data providers.',
  'While reasonable efforts may be made to ensure the information is accurate and up to date, Supply Guard does not independently verify or guarantee the accuracy, completeness, reliability, or currency of the information presented.',
  'The information is provided on an "as is" and "as available" basis, without any representations or warranties, express or implied, to the fullest extent permitted by applicable law.',
  'Supply Guard accepts no liability, to the fullest extent permitted by law, for any errors, omissions, inaccuracies, outdated information, or any loss or damage arising from reliance on the information contained in this profile.',
  'Users are responsible for independently verifying any information before making commercial, procurement, compliance, or other business decisions.',
  'The inclusion of a supplier within Supply Guard does not constitute an endorsement, certification, or recommendation of that supplier.',
];

type ProvenanceCopy = {
  pillLabel: string;
  cardLine: string | null;
  noticeTitle: string | null;
  noticeBody: (supplierName: string | null | undefined) => string | null;
  cardTooltip: string;
  contactTitle: string | null;
  contactBody: (supplierName: string | null | undefined) => string | null;
  vendorBanner: (supplierName: string | null | undefined) => string | null;
};

function nameOrFallback(supplierName: string | null | undefined): string {
  const trimmed = supplierName?.trim();
  return trimmed ? trimmed : 'The supplier';
}

const none = () => null;

export const PROVENANCE_COPY: Record<ProfileProvenance, ProvenanceCopy> = {
  supplier_attested: {
    pillLabel: 'Verified',
    cardLine: null,
    noticeTitle: null,
    noticeBody: none,
    cardTooltip: 'This supplier has claimed and maintains this profile.',
    contactTitle: null,
    contactBody: none,
    vendorBanner: none,
  },
  supply_guard_managed: {
    pillLabel: 'Supply Guard Managed',
    cardLine: 'Maintained by Supply Guard from public sources',
    noticeTitle: 'This profile is maintained by Supply Guard from publicly available information.',
    noticeBody: (supplierName) =>
      `${nameOrFallback(supplierName)} has not claimed or verified it. Please verify details independently before relying on them.`,
    cardTooltip: 'Maintained by Supply Guard from public sources. Read the disclaimer on the profile.',
    contactTitle: 'Maintained by Supply Guard',
    contactBody: (supplierName) =>
      `Questions about this profile: team@supply-guard.io. Supply Guard does not speak for ${nameOrFallback(supplierName)}.`,
    vendorBanner: (supplierName) =>
      `You are maintaining this profile on behalf of Supply Guard. Customers see the "Supply Guard Managed" badge and the public-sources disclaimer. If ${nameOrFallback(supplierName)} claims the profile, your team is removed.`,
  },
  public_sources: {
    pillLabel: 'Unclaimed',
    cardLine: 'Compiled from public sources',
    noticeTitle: 'Compiled from publicly available information.',
    noticeBody: (supplierName) => `${nameOrFallback(supplierName)} has not claimed this profile.`,
    cardTooltip: 'Compiled from public sources. Read the disclaimer on the profile.',
    contactTitle: null,
    contactBody: none,
    vendorBanner: none,
  },
};
